<template>
  <div class="dating-page">
    <van-nav-bar fixed placeholder class="page-nav" :border="false">
      <template #title>
        <span class="page-title">{{ $t('ui.dating.title') }}</span>
      </template>
      <template #right>
        <van-icon name="home-o" class="home-icon" @click="$router.push('/Home')" />
      </template>
    </van-nav-bar>

    <div class="page-content">
      <section class="category-section">
        <div class="category-scroll">
          <button
            v-for="item in categoryList"
            :key="String(item.id)"
            type="button"
            class="category-chip"
            :class="{ active: activeCategory === item.id }"
            @click="switchCategory(item.id)"
          >
            {{ item.name }}
          </button>
        </div>
      </section>

      <section v-if="loading" class="loading-section">
        <van-loading color="#e6b98a" size="24px" />
      </section>

      <section v-else-if="profiles.length" class="dating-grid">
        <article
          v-for="item in profiles"
          :key="item.id"
          class="dating-card"
          @click="toDetail(item.id)"
        >
          <img :src="item.cover" :alt="item.stageName" class="dating-image">
          <img src="https://m.sincereloves.online/icon/lives.gif" alt="live" class="live-icon">
          <div class="verified-tag">{{ $t('ui.common.verified') }}</div>

          <div class="card-overlay">
            <div class="card-top">
              <span class="id-text">{{ $t('ui.common.id_prefix') }}{{ item.id }}</span>
              <span class="location-tag">{{ item.location }}</span>
            </div>
            <div class="name-text">{{ item.stageName }}</div>
            <div class="score-row">
              <img src="https://m.sincereloves.online/icon/icon_love.png" alt="score" class="score-icon">
              <span class="score-text">{{ item.score }}</span>
            </div>
          </div>
        </article>
      </section>

      <section v-else class="empty-section">
        <img src="https://m.sincereloves.online/logo.png" alt="empty" class="empty-logo">
        <p>{{ $t('ui.dating.empty') }}</p>
      </section>
    </div>

  </div>
</template>

<script>
const ALL_CATEGORY = 'all';

export default {
  data() {
    return {
      loading: false,
      activeCategory: ALL_CATEGORY,
      scrollbarStyleId: 'choose-scrollbar-style',
      addressTabs: [],
      profiles: []
    };
  },
  computed: {
    categoryList() {
      return [{ id: ALL_CATEGORY, name: this.$t('ui.dating.all') }, ...this.addressTabs];
    }
  },
  methods: {
    normalizeAssetUrl(url) {
      if (!url || typeof url !== 'string') {
        return '';
      }

      const origin = 'https://admin699.tinlover.com';

      if (/^https?:\/\/127\.0\.0\.1\//.test(url)) {
        return url.replace(/^https?:\/\/127\.0\.0\.1\//, `${origin}/`);
      }

      if (/^https?:\/\/localhost:8081\//.test(url)) {
        return url.replace(/^https?:\/\/localhost:8081\//, `${origin}/`);
      }

      if (url.startsWith('/')) {
        return `${origin}${url}`;
      }

      return url;
    },
    localizeFlag(flag) {
      const rawFlag = typeof flag === 'string' ? flag : '';
      if (!rawFlag) {
        return '';
      }

      const parts = rawFlag.split('|');
      const lang = localStorage.getItem('lang') || 'zh';

      if (lang === 'en') {
        return parts[1] || parts[0] || '';
      }
      if (lang === 'ja') {
        return parts[2] || parts[0] || '';
      }
      if (lang === 'kr') {
        return parts[3] || parts[0] || '';
      }

      return parts[0] || '';
    },
    normalizeAddressName(name) {
      if (typeof name !== 'string') {
        return '';
      }

      return name.split(',')[0].trim();
    },
    normalizeProfile(item) {
      const safeItem = item && typeof item === 'object' ? item : {};
      const numeric = Number(safeItem.number || safeItem.chakan || safeItem.dianzan || 9.9);
      const location = this.localizeFlag(safeItem.flag) || this.normalizeAddressName(safeItem.name) || this.$t('ui.common.default_location');

      return {
        ...safeItem,
        cover: this.normalizeAssetUrl(safeItem.img_url),
        stageName: safeItem.xuanfei_name || `VIP${safeItem.id || ''}`,
        location,
        score: Math.min(10, Math.max(9.6, numeric)).toFixed(1)
      };
    },
    fetchProfilesByCategory(categoryId) {
      return this.$http({
        method: 'get',
        url: 'xuanfeilist',
        data: { id: categoryId, page: 1 }
      }).then((res) => {
        const list = Array.isArray(res && res.data) ? res.data : [];
        return list.map((item) => this.normalizeProfile(item));
      }).catch(() => []);
    },
    getAddressList() {
      return this.$http({
        method: 'get',
        url: 'address_list'
      }).then((res) => {
        const groups = Array.isArray(res && res.data) ? res.data : [];
        const list = groups.flat().filter((item) => item && typeof item === 'object');

        this.addressTabs = list.map((item) => ({
          id: item.id,
          name: this.normalizeAddressName(item.name) || this.$t('ui.dating.area_fallback', { id: item.id })
        }));
      }).catch(() => {
        this.addressTabs = [];
      });
    },
    getDatingList(categoryId = this.activeCategory) {
      this.loading = true;

      const task = categoryId === ALL_CATEGORY
        ? Promise.all(this.addressTabs.map((item) => this.fetchProfilesByCategory(item.id))).then((groups) => {
          const merged = groups.flat();
          const uniqueMap = new Map();

          merged.forEach((item) => {
            if (!uniqueMap.has(item.id)) {
              uniqueMap.set(item.id, item);
            }
          });

          return Array.from(uniqueMap.values());
        })
        : this.fetchProfilesByCategory(categoryId);

      task.then((list) => {
        this.profiles = list;
      }).finally(() => {
        this.loading = false;
      });
    },
    switchCategory(categoryId) {
      if (this.activeCategory === categoryId && this.profiles.length) {
        return;
      }

      this.activeCategory = categoryId;
      this.getDatingList(categoryId);
    },
    hideRootScrollbar() {
      if (typeof document === 'undefined') {
        return;
      }

      const root = document.documentElement;
      const body = document.body;

      root.style.setProperty('scrollbar-width', 'none');
      body.style.setProperty('scrollbar-width', 'none');
      root.style.setProperty('-ms-overflow-style', 'none');
      body.style.setProperty('-ms-overflow-style', 'none');

      if (!document.getElementById(this.scrollbarStyleId)) {
        const style = document.createElement('style');
        style.id = this.scrollbarStyleId;
        style.textContent = `
html::-webkit-scrollbar,
body::-webkit-scrollbar {
  width: 0 !important;
  height: 0 !important;
  display: none !important;
}
        `;
        document.head.appendChild(style);
      }
    },
    restoreRootScrollbar() {
      if (typeof document === 'undefined') {
        return;
      }

      const root = document.documentElement;
      const body = document.body;
      const style = document.getElementById(this.scrollbarStyleId);

      root.style.removeProperty('scrollbar-width');
      body.style.removeProperty('scrollbar-width');
      root.style.removeProperty('-ms-overflow-style');
      body.style.removeProperty('-ms-overflow-style');

      if (style && style.parentNode) {
        style.parentNode.removeChild(style);
      }
    },
    toDetail(id) {
      this.$router.push({ path: '/detail', query: { id } });
    }
  },
  mounted() {
    this.hideRootScrollbar();
  },
  beforeDestroy() {
    this.restoreRootScrollbar();
  },
  created() {
    this.getAddressList().then(() => {
      this.getDatingList(ALL_CATEGORY);
    });
  }
};
</script>

<style lang="less" scoped>
.dating-page {
  min-height: 100vh;
  background: #000;
  color: #fff;
}

.page-nav {
  min-height: 98px;
  background: #1c1c1e;
}

::v-deep .page-nav .van-nav-bar {
  background: #1c1c1e;
  box-shadow: none;
}

::v-deep .page-nav .van-nav-bar__content {
  height: 88px;
}

::v-deep .page-nav .van-nav-bar__left,
::v-deep .page-nav .van-nav-bar__right {
  padding: 0 18px;
}

.page-title {
  color: #e6b98a;
  font-size: 28px;
  font-weight: 700;
  line-height: 1;
}

.home-icon {
  color: #e6b98a;
  font-size: 38px;
}

.page-content {
  padding: 12px 10px 98px;
}

.category-section {
  margin-bottom: 16px;
}

.category-scroll {
  display: flex;
  gap: 10px;
  overflow-x: auto;
  padding-bottom: 4px;
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.category-scroll::-webkit-scrollbar {
  display: none;
}

.category-chip {
  flex-shrink: 0;
  min-width: 70px;
  padding: 10px 16px;
  border: 1px solid #766350;
  border-radius: 999px;
  background: linear-gradient(180deg, #2b2d33, #18191e);
  color: #c8a47f;
  font-size: 13px;
  font-weight: 600;
}

.category-chip.active {
  background: linear-gradient(90deg, #efcba7, #dcab76);
  color: #1c1c1e;
  border-color: transparent;
}

.loading-section,
.empty-section {
  display: flex;
  min-height: 50vh;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  color: #bd9777;
}

.empty-logo {
  width: 72px;
  height: 72px;
  margin-bottom: 14px;
  object-fit: contain;
}

.dating-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.dating-card {
  position: relative;
  overflow: hidden;
  min-height: 318px;
  border: 4px solid #766350;
  border-radius: 16px;
  background: #15171d;
}

.dating-image {
  display: block;
  width: calc(100% - 8px);
  height: calc(100% - 8px);
  margin: 4px;
  object-fit: cover;
}

.live-icon {
  position: absolute;
  left: 12px;
  top: 12px;
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.verified-tag {
  position: absolute;
  top: 20px;
  right: -30px;
  padding: 2px 38px;
  background: linear-gradient(90deg, #efcba7, #dcab76);
  color: #111;
  font-size: 12px;
  font-style: italic;
  transform: rotate(45deg);
}

.card-overlay {
  position: absolute;
  right: 4px;
  bottom: 4px;
  left: 4px;
  padding: 12px;
  background: rgba(0, 0, 0, 0.5);
}

.card-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
}

.id-text {
  color: #e6b98a;
  font-size: 12px;
  font-weight: 700;
}

.location-tag {
  padding: 3px 10px;
  border-radius: 10px;
  background: linear-gradient(90deg, #efcba7, #dcab76);
  color: #4a4947;
  font-size: 12px;
}

.name-text {
  margin-top: 8px;
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.score-row {
  display: flex;
  align-items: center;
  margin-top: 8px;
}

.score-icon {
  width: 16px;
  height: 16px;
  margin-right: 4px;
  object-fit: contain;
}

.score-text {
  color: #fff;
  font-size: 14px;
}

</style>
